import { PageLink as Link } from '../app/navigation';
import {
  ArrowRight,
  BookOpen,
  FileSearch,
  GitCompareArrows,
  History,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { useWorks, useStats } from '../api/hooks';
import { formatLegalTitle } from '../components/ui/formatLegalTitle';
import './home.css';

export function Component() {
  const works = useWorks();
  const stats = useStats();
  const example =
    works.data?.find((work) => work.work_id === 'nation-building-tax-2009-9') ??
    works.data?.[0];
  return (
    <div className="sedlex-home">
      <section className="sedlex-home-hero">
        <div className="sedlex-home-copy">
          <span className="sedlex-eyebrow">
            <span className="sedlex-status-dot" />
            Sri Lankan legislation, connected
          </span>
          <h1>
            Read the law.
            <br />
            <em>Understand its history.</em>
          </h1>
          <p>
            A clearer way to explore Sri Lankan Acts and amendments. Read a dated version,
            see what changed, and follow the evidence behind the wording.
          </p>
          <div className="sedlex-home-actions">
            <Link className="sedlex-action-primary" to="/corpus">
              Browse the corpus
              <ArrowRight aria-hidden="true" />
            </Link>
            <Link className="sedlex-action-secondary" to="/search">
              <Search aria-hidden="true" />
              Search provisions
            </Link>
          </div>
          <div className="sedlex-home-metrics" aria-label="Current research collection">
            <span>
              <strong>{stats.data?.works ?? '—'}</strong> Act families
            </span>
            <span>
              <strong>{stats.data?.versions ?? '—'}</strong> dated versions
            </span>
            <span>
              <strong>{stats.data?.provisions.toLocaleString() ?? '—'}</strong> indexed
              provisions
            </span>
          </div>
          <span className="sedlex-home-caption">
            A growing research collection. Every date and source matters.
          </span>
        </div>
        <div className="sedlex-home-document" aria-label="An example Act family">
          <div className="sedlex-home-document-top">
            <span>
              <BookOpen aria-hidden="true" />
              Inside an Act family
            </span>
            <span className="sedlex-research-label">Research corpus</span>
          </div>
          <div className="sedlex-home-document-content">
            <span className="sedlex-eyebrow">ONE ACT. ITS LEGISLATIVE HISTORY.</span>
            <h2>
              {example
                ? formatLegalTitle(example.short_title)
                : 'Sri Lankan statutory history'}
            </h2>
            <p>From the principal enactment to the latest version in the collection.</p>
            <div className="sedlex-home-timeline">
              <div>
                <span className="sedlex-home-timeline-dot" />
                <small>{example?.first_enacted_date?.slice(0, 4) ?? example?.source_year ?? 'Enactment'}</small>
                <strong>Principal Act</strong>
                <p>The starting text</p>
              </div>
              <div>
                <span className="sedlex-home-timeline-dot" />
                <small>
                  {example ? `${example.amendment_count} amendments` : 'Amendments'}
                </small>
                <strong>Changes over time</strong>
                <p>Wording and sources connected</p>
              </div>
              <div>
                <span className="sedlex-home-timeline-dot" />
                <small>
                  {example?.latest_effective_date?.slice(0, 4) ?? example?.latest_source_year ?? 'Latest available'}
                </small>
                <strong>Latest indexed version</strong>
                <p>Reconstructed text with review status</p>
              </div>
            </div>
            {example && (
              <Link className="sedlex-home-preview-link" to={`/work/${example.work_id}`}>
                Explore this Act
                <ArrowRight aria-hidden="true" />
              </Link>
            )}
          </div>
          <div className="sedlex-home-document-bottom">
            <ShieldCheck aria-hidden="true" />
            Source evidence and unresolved changes stay visible.
          </div>
        </div>
      </section>
      <section className="sedlex-home-workflow" aria-labelledby="workflow-title">
        <div className="sedlex-home-section-title">
          <span className="sedlex-eyebrow">A SIMPLE RESEARCH WORKFLOW</span>
          <h2 id="workflow-title">Find your starting point.</h2>
          <p>For a first visit, a research question, or a closer reading.</p>
        </div>
        <div className="sedlex-home-feature-grid">
          <Link to="/corpus">
            <span className="sedlex-feature-number">01 / EXPLORE</span>
            <BookOpen aria-hidden="true" />
            <h3>Find an Act</h3>
            <p>Browse Act families and choose the legislation you want to understand.</p>
            <span className="sedlex-feature-link">
              Browse legislation
              <ArrowRight aria-hidden="true" />
            </span>
          </Link>
          <Link to="/search">
            <span className="sedlex-feature-number">02 / RESEARCH</span>
            <FileSearch aria-hidden="true" />
            <h3>Find the relevant wording</h3>
            <p>
              Search provisions by a concept, with the Act and effective date alongside
              each result.
            </p>
            <span className="sedlex-feature-link">
              Search provisions
              <ArrowRight aria-hidden="true" />
            </span>
          </Link>
          <Link to={example ? `/work/${example.work_id}/diff` : '/corpus'}>
            <span className="sedlex-feature-number">03 / UNDERSTAND</span>
            <GitCompareArrows aria-hidden="true" />
            <h3>Follow what changed</h3>
            <p>
              Compare dated versions, then trace the current wording to its source
              enactment.
            </p>
            <span className="sedlex-feature-link">
              Explore version changes
              <ArrowRight aria-hidden="true" />
            </span>
          </Link>
        </div>
      </section>
      <section className="sedlex-home-about">
        <div>
          <History aria-hidden="true" />
          <span className="sedlex-eyebrow">BUILT FOR CAREFUL READING</span>
          <h2>A version is more than a document.</h2>
          <p>
            Each Act family connects its principal text, amendments, reconstructed
            versions, and available source evidence. A dated version represents the text
            the system has reconstructed for that point in its history.
          </p>
        </div>
        <div className="sedlex-home-research-note">
          <ShieldCheck aria-hidden="true" />
          <h3>Research, with its limits visible.</h3>
          <p>
            SLAD is a research tool. Reconstructed versions can contain unresolved
            changes and are not official consolidated legislation. Check the source
            enactments and the quality notes before relying on the text.
          </p>
          <Link to="/quality">
            How to read the data quality notes
            <ArrowRight aria-hidden="true" />
          </Link>
        </div>
      </section>
    </div>
  );
}
Component.displayName = 'PublicHomePage';
